/// <reference lib="dom" />
/**
 * webFinaleEffects.web.ts
 * Canvas2D draw helpers for finale set-pieces: gate, ramp, dust, sonic boom.
 *
 * Extracted from webFinaleDraw.web.ts to keep both files under the 300-line
 * AGENTS.md limit. All functions are stateless reads of finaleState.
 *
 * Web-only (.web.ts) — never bundled on native.
 *
 * Phase 3 — Art, Audio & Polish.
 *
 * @module webFinaleEffects
 */

// DECISION: Import directly from finaleState to avoid pulling in the full
// FinaleSequence state machine and its heavy dependencies.
import { finaleState } from '../scenes/finaleState';

// ── Gate colours ──────────────────────────────────────────────────────────

const GATE_STONE   = '#4a4a5a';
const GATE_TRIM    = '#6a6a7a';
const GATE_DOOR    = '#2a2a3a';
const GATE_CRYSTAL = '#00e5ff';

// ── Ramp colours ─────────────────────────────────────────────────────────

const RAMP_BODY = '#5a5a6a';
const RAMP_EDGE = '#3a3a4a';

/**
 * Draw the launch gate archway with sliding doors.
 *
 * @param ctx  - Canvas 2D context.
 * @param camX - Camera X offset.
 * @param camY - Camera Y offset.
 */
export function drawGate(ctx: CanvasRenderingContext2D, camX: number, camY: number): void {
  const gx = finaleState.gateX - camX;
  const gy = finaleState.gateTopY - camY;
  const gw = finaleState.gateW;
  const gh = finaleState.gateH;

  // Stone archway frame (outer border).
  ctx.fillStyle = GATE_STONE;
  // Left pillar.
  ctx.fillRect(gx - 4, gy, 8, gh);
  // Right pillar.
  ctx.fillRect(gx + gw - 4, gy, 8, gh);
  // Top lintel.
  ctx.fillRect(gx - 4, gy - 4, gw + 8, 8);

  // Trim lines.
  ctx.fillStyle = GATE_TRIM;
  ctx.fillRect(gx - 2, gy + 2, 4, gh - 4);
  ctx.fillRect(gx + gw - 2, gy + 2, 4, gh - 4);

  // Crystal accents on pillars — save/restore to avoid leaking globalAlpha.
  ctx.save();
  const crystalAlpha = 0.6 + Math.sin(Date.now() * 0.004) * 0.3;
  ctx.globalAlpha = crystalAlpha;
  ctx.fillStyle = GATE_CRYSTAL;
  ctx.fillRect(gx - 1, gy + gh * 0.3, 2, 6);
  ctx.fillRect(gx + gw - 1, gy + gh * 0.3, 2, 6);
  ctx.restore();

  // Sliding doors — open from centre outward.
  const doorW = gw / 2;
  const openOffset = finaleState.gateOpen * doorW;

  ctx.fillStyle = GATE_DOOR;
  // Left door slides left.
  ctx.fillRect(gx - openOffset, gy + 4, doorW, gh - 4);
  // Right door slides right.
  ctx.fillRect(gx + doorW + openOffset, gy + 4, doorW, gh - 4);
}

/**
 * Draw the tilting ramp platform.
 *
 * @param ctx  - Canvas 2D context.
 * @param camX - Camera X offset.
 * @param camY - Camera Y offset.
 */
export function drawRamp(ctx: CanvasRenderingContext2D, camX: number, camY: number): void {
  const rx = finaleState.rampX - camX;
  const ry = finaleState.rampBaseY - camY;
  const rw = finaleState.rampW;
  const angle = (finaleState.rampAngle * Math.PI) / 180;

  ctx.save();
  // DECISION: Pivot at the left edge of the ramp (ground level) so it tilts
  // upward from left to right, matching the ship launch angle.
  ctx.translate(rx, ry);
  ctx.rotate(-angle);

  // Ramp body.
  ctx.fillStyle = RAMP_BODY;
  ctx.fillRect(0, -6, rw, 6);

  // Edge highlight.
  ctx.fillStyle = RAMP_EDGE;
  ctx.fillRect(0, -6, rw, 2);

  // Piston struts (2 visible mechanical supports).
  ctx.fillStyle = '#7a7a8a';
  ctx.fillRect(rw * 0.25, 0, 3, 10);
  ctx.fillRect(rw * 0.75, 0, 3, 10);

  ctx.restore();
}

/**
 * Draw dust particles under the ramp/ship during mechanical sequences.
 *
 * @param ctx  - Canvas 2D context.
 * @param camX - Camera X offset.
 * @param camY - Camera Y offset.
 */
export function drawDust(ctx: CanvasRenderingContext2D, camX: number, camY: number): void {
  if (!finaleState.dustActive) return;

  const rx = finaleState.rampX - camX;
  const ry = finaleState.rampBaseY - camY;
  const rw = finaleState.rampW;

  // DECISION: Deterministic pseudo-particles using timer as seed — no
  // allocation, no object pool needed for a visual-only effect.
  const t = finaleState.timer;

  // Save/restore so per-particle globalAlpha changes don't leak to caller.
  ctx.save();
  ctx.fillStyle = 'rgba(180, 180, 190, 0.4)';
  for (let i = 0; i < 8; i++) {
    const phase = (t * 2 + i * 0.7) % 1;
    const px = rx + (i / 8) * rw + Math.sin(i * 3.7) * 8;
    const py = ry + phase * 12;
    const size = 2 + Math.sin(i * 2.1 + t) * 1;
    ctx.globalAlpha = 0.5 * (1 - phase);
    ctx.fillRect(px, py, size, size);
  }
  ctx.restore();
}

/**
 * Draw the expanding sonic boom ring after ship exits screen.
 *
 * @param ctx  - Canvas 2D context.
 * @param camX - Camera X offset.
 * @param camY - Camera Y offset.
 */
export function drawSonicBoom(ctx: CanvasRenderingContext2D, camX: number, camY: number): void {
  if (finaleState.sonicBoom <= 0) return;

  // Centre at the ship's last known position (ramp area).
  const cx = finaleState.rampX + finaleState.rampW / 2 - camX;
  const cy = finaleState.rampBaseY - 40 - camY;
  const radius = finaleState.sonicBoom * 120;

  // Save/restore so globalAlpha change doesn't leak to caller.
  ctx.save();
  ctx.strokeStyle = GATE_CRYSTAL;
  ctx.lineWidth = 2;
  ctx.globalAlpha = 1 - finaleState.sonicBoom;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}
